import * as React from "react";
import { useSubscription } from "../../hooks/useSubscription";
import {
  ScheduledTournamentParticipantRequestsSubscription,
  ScheduledTournamentParticipantRequestsSubscriptionVariables,
} from "./graphql/ScheduledTournamentEventsSubscription.generated";
import {
  AcceptScheduledTournamentPairRequestMutation,
  AcceptScheduledTournamentPairRequestMutationVariables,
} from "./graphql/AcceptScheduledTournamentPairRequest.generated";
import { loader } from "graphql.macro";
import { pipe, map } from "wonka";
import produce from "immer";
import { ProfileImage } from "../ProfileImage";
import { Trans, useTranslation } from "react-i18next";
import { useCountDown } from "../../hooks/useCountDown";
import { useBehaviourSubject, useSource } from "../../hooks/useSource";
import { useClient } from "urql";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { ButtonWithSound, LinkWithSound, useAudio } from "../AudioProvider";
import { useSyncedTimeProvider } from "../SyncedTimeProvider";
import styles from "../Area/TablesList/InstantTournaments.module.scss";
import classNames from "classnames";
import closeIcon from "../Area/TablesList/assets/close-icon.png";
import invitationLogo from "../Area/TablesList/assets/invitation-logo.png";
import coin from "../Area/TablesList/assets/coin.png";
import goldCoins from "../Area/TablesList/assets/gold-coins.png";
import players from "../Area/TablesList/assets/players-icon.png";
import formatIcon from "../Area/TablesList/assets/format.png";
import divider from "../Area/TablesList/assets/divider.png";
import { useMutation } from "../../hooks/useMutation";
import {
  DeclineScheduledTournamentPairRequestMutation,
  DeclineScheduledTournamentPairRequestMutationVariables,
} from "./graphql/DeclineScheduledTournamentPairRequest.generated";
import { useHistory } from "react-router-dom";
import { ParticipantRequestStatus } from "../../graphql/generated";
import eventBg from "../Area/TablesList/assets/event-bg.png";
import accepted from "../Area/TablesList/assets/accepted.png";
import rejected from "../Area/TablesList/assets/rejected.png";
import { useUserId } from "../UserProvider";
import { useEffect, useMemo } from "react";

const ACCEPT_REQUEST_MUTATION = loader(
  "./graphql/AcceptScheduledTournamentPairRequest.graphql"
);

const DENY_REQUEST_MUTATION = loader(
  "./graphql/DeclineScheduledTournamentPairRequest.graphql"
);

const SCHEDULED_TOURNAMENT_EVENTS_SUBSCRIPTION = loader(
  "./graphql/ScheduledTournamentEventsSubscription.graphql"
);

const EVENT_AVATAR_SIZE = {
  width: 69,
  height: 69,
};

type Invitation = NonNullable<
  ScheduledTournamentParticipantRequestsSubscription["scheduledTournamentParticipantRequests"]
>;

type Message =
  | {
      type: "ADD_INVITATION";
      payload: Invitation;
    }
  | {
      type: "CLEAR_EXPIRED";
    }
  | {
      type: "POP";
    }
  | {
      type: "CLEAR";
    };

const reducer = produce((state: Array<Invitation>, message: Message) => {
  switch (message.type) {
    case "ADD_INVITATION":
      state.push(message.payload);
      return state;
    case "POP":
      return state.slice(1);
    case "CLEAR":
      state = [];
      return state;
    default:
      return state;
  }
});

const CloseButton = ({ closeHandler }: { closeHandler: () => void }) => {
  return (
    <div className="flex justify-center absolute right-0 top-0">
      <ButtonWithSound className="btn btn-lg p-0" onClick={closeHandler}>
        <img src={closeIcon} alt="close" />
      </ButtonWithSound>
    </div>
  );
};

const InvitationData = ({
  onCancel,
  onAccept,
  onDeny,
  onRedirect,
  invitation,
  onTick,
}: {
  onCancel: (id: string) => void;
  onAccept: (id: string) => void;
  onDeny: (id: string) => void;
  onRedirect: (id: string) => void;
  invitation: Invitation;
  onTick?: (remaining: number) => void;
}) => {
  const { t } = useTranslation();

  const partnerName = invitation?.toMe
    ? invitation?.toMe.fromUserName
    : invitation.fromMe?.toUserName;
  const isMatchLocked = useMemo(() => {
    return (
      invitation?.fromMe?.requestStatus === ParticipantRequestStatus.Accepted ||
      invitation?.toMe?.requestStatus === ParticipantRequestStatus.Accepted
    );
  }, [invitation]);

  const isRejected = useMemo(() => {
    return (
      invitation?.fromMe?.requestStatus === ParticipantRequestStatus.Declined
    );
  }, [invitation]);

  const isPicked = useMemo(() => {
    return invitation?.toMe?.requestStatus === ParticipantRequestStatus.Pending;
  }, [invitation]);

  return (
    <div
      className={classNames(
        "flex flex-col relative bg-blue-800",
        styles.tournamentInvitation
      )}
    >
      <div className="flex justify-between items-center px-8 pt-3 pb-2 gap-8 absolute left-0 top-0 w-full">
        <div className="flex items-center gap-3 relative w-full">
          <div className={styles.eventPhoto}>
            <ProfileImage
              id="693ac377-d9de-338c-a69d-440da1903e40"
              name="dsvbsdbvsv"
              className="bg-gray-400 rounded-full"
              {...EVENT_AVATAR_SIZE}
            />
          </div>
          <img src={eventBg} className={styles.invitationLogo} alt="img" />
          {isMatchLocked && (
            <img src={accepted} className={styles.eventStatus} alt="img" />
          )}
          {isRejected && (
            <img src={rejected} className={styles.eventStatus} alt="img" />
          )}
          <CloseButton
            closeHandler={() => onCancel(invitation.scheduledTournamentId)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        <div className="flex flex-col px-8 gap-4 items-center text-center">
          <div className="flex flex-col gap-1 items-center text-center text-white text-base">
            <span className="font-semibold text-yellow-400 text-2xl capitalize">
              {isPicked && t("instant_tournament.you_picked")}
              {isMatchLocked && t("instant_tournament.match_locked")}
              {isRejected && t("instant_tournament.rejected")}
            </span>

            {isPicked && (
              <>
                <p>
                  <Trans
                    className="flex"
                    i18nKey="instant_tournament.wants_to_be_partner"
                    components={{ name: <span className="font-bold" /> }}
                    values={{
                      tournamentName: invitation?.tournamentName,
                      partnerName: invitation.toMe?.fromUserName,
                    }}
                  />
                </p>
                <p>{t("instant_tournament.will_you_accept")}</p>
              </>
            )}
            {isMatchLocked && (
              <>
                <p>
                  <Trans
                    className="flex"
                    i18nKey="instant_tournament.you_are_now_partners"
                    components={{ name: <span className="font-bold" /> }}
                    values={{ player: partnerName }}
                  />
                </p>
                <p>{t("instant_tournament.get_ready_to_win")}</p>
              </>
            )}
            {isRejected && (
              <p>
                <Trans
                  className="flex"
                  i18nKey="instant_tournament.pick_new_partner"
                  components={{ name: <span className="font-bold" /> }}
                  values={{ player: invitation?.fromMe?.toUserName }}
                />
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-center gap-4">
          {isPicked && (
            <>
              <ButtonWithSound
                onClick={() => onAccept(invitation.toMe?.fromUserId!)}
                className={classNames(
                  "px-4",
                  "text-white",
                  "rounded-md",
                  "font-semibold",
                  "btn",
                  "btn-green-600",
                  "w-fit",
                  "text-base"
                )}
              >
                {t("accept")}
              </ButtonWithSound>
              <ButtonWithSound
                onClick={() => onDeny(invitation.toMe?.fromUserId!)}
                className={classNames(
                  "px-4",
                  "text-black",
                  "rounded-md",
                  "font-semibold",
                  "btn",
                  "btn-gray-400",
                  "w-fit",
                  "text-base"
                )}
              >
                {t("deny")}
              </ButtonWithSound>
            </>
          )}
          {(isRejected || isMatchLocked) && (
            <ButtonWithSound
              onClick={() => onRedirect(invitation.scheduledTournamentId)}
              className={classNames(
                "px-4",
                "text-white",
                "rounded-md",
                "font-semibold",
                "btn",
                "w-fit",
                "text-base",
                isRejected ? "btn-orange-600" : "btn-green-600"
              )}
            >
              {isRejected
                ? t("instant_tournament.select_another_partner")
                : t("instant_tournament.let_go")}
            </ButtonWithSound>
          )}
        </div>
      </div>
    </div>
  );
};

const Popup = ({
  invitations,
  onCancel,
  onAccept,
  onDeny,
  onRedirect,
}: {
  invitations: Invitation;
  onCancel: (id: string) => void;
  onAccept: (id: string) => void;
  onDeny: (id: string) => void;
  onRedirect: (id: string) => void;
}) => {
  const audio = useAudio();

  return (
    <div className="absolute bg-black-alpha-30 top-0 left-0 right-0 bottom-0 z-50">
      <AbsoluteCenter>
        <InvitationData
          invitation={invitations}
          onCancel={onCancel}
          onAccept={onAccept}
          onDeny={onDeny}
          onRedirect={onRedirect}
          onTick={() => audio.play("timer")}
        />
      </AbsoluteCenter>
    </div>
  );
};

export const ScheduledTournamentEvents = () => {
  const { push } = useHistory();
  const syncedTime = useSyncedTimeProvider();
  const myUserId = useUserId();

  const [{ data }] = useSubscription<
    unknown,
    ScheduledTournamentParticipantRequestsSubscription,
    ScheduledTournamentParticipantRequestsSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_EVENTS_SUBSCRIPTION,
    throwOnError: false,
  });

  const [, denyRequest] = useMutation<
    DeclineScheduledTournamentPairRequestMutation,
    DeclineScheduledTournamentPairRequestMutationVariables
  >(DENY_REQUEST_MUTATION, false);

  const [, acceptRequest] = useMutation<
    AcceptScheduledTournamentPairRequestMutation,
    AcceptScheduledTournamentPairRequestMutationVariables
  >(ACCEPT_REQUEST_MUTATION, false);

  const [subscription$$, update] = useBehaviourSubject(
    React.useMemo<Message | null>(
      () =>
        data?.scheduledTournamentParticipantRequests
          ? {
              type: "ADD_INVITATION",
              payload: { ...data?.scheduledTournamentParticipantRequests },
            }
          : null,
      [data]
    )
  );

  const state = useSource(
    React.useMemo(() => {
      return pipe(
        subscription$$,
        map((message: Message | null) => message)
      );
    }, [subscription$$]),
    null
  );

  const onCancel = React.useCallback(
    (id: string) => {
      update({ type: "CLEAR" });
    },
    [update]
  );

  const handleAcceptInvitation = (id: string) => {
    acceptRequest({
      input: {
        scheduledTournamentId: data?.scheduledTournamentParticipantRequests
          ?.scheduledTournamentId!,
        fromUserId: id,
      },
    }).then(() => onCancel(id));
  };

  const handleDenyInvitation = (id: string) => {
    denyRequest({
      input: {
        scheduledTournamentId: data?.scheduledTournamentParticipantRequests
          ?.scheduledTournamentId!,
        fromUserId: id,
      },
    }).then(() => onCancel(id));
  };

  const handleRedirect = async (id: string) => {
    const tournamentData = data?.scheduledTournamentParticipantRequests;

    await onCancel(id);
    await push(
      `/area/${tournamentData?.roomId}:${tournamentData?.gameId}/scheduled-tournaments/current/${id}`
    );
  };

  return (
    <>
      {state &&
        state.type === "ADD_INVITATION" &&
        data?.scheduledTournamentParticipantRequests?.toMe?.requestStatus !==
          ParticipantRequestStatus.Declined &&
        data?.scheduledTournamentParticipantRequests?.fromMe?.requestStatus !==
          ParticipantRequestStatus.Pending &&
        data?.scheduledTournamentParticipantRequests?.fromMe?.requestStatus !==
          ParticipantRequestStatus.Free &&
        data?.scheduledTournamentParticipantRequests?.toMe?.requestStatus !==
          ParticipantRequestStatus.Free && (
          // @ts-ignore
          <Popup
            invitations={state?.payload}
            onCancel={onCancel}
            onAccept={handleAcceptInvitation}
            onDeny={handleDenyInvitation}
            onRedirect={handleRedirect}
          />
        )}
    </>
  );
};
